import { buildColumn, EMPTY_VALUE, statusColumn, type DataTableColumn } from '@odo/shared-ui';
import type { Role } from '../../../services/roles';
import { formatCreatedDate, formatRelativeActivity } from '../../../utils/dates';
import { TeamColorTile } from './TeamColorTile';
import { TEAM_SCOPE_LABEL, TEAM_SCOPE_TONE } from './teamScopeColor';

export type TeamsSortBy = 'name' | 'created';

const TD_PADDING = 'px-3 first:pl-5 last:pr-4';
const TH_PADDING = '!px-3 first:!pl-5 last:!pr-4';

// Nulls last is unreachable here (createdAt is never null), so a plain
// numeric compare is enough; name is still the tiebreaker for equal instants.
export function compareTeams(a: Role, b: Role, sortBy: TeamsSortBy): number {
    if (sortBy === 'created') {
        return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() ||
            a.name.localeCompare(b.name)
        );
    }
    return a.name.localeCompare(b.name);
}

// No member-count column: the API has no batch count, and one GET
// /roles/:id/users per row would be an N+1 the list can't afford.
export function buildTeamColumns(): DataTableColumn<Role, TeamsSortBy>[] {
    return [
        buildColumn<Role, TeamsSortBy>(
            {
                key: 'team',
                label: 'Team',
                width: '45%',
                sortable: true,
                sortKey: 'name',
                cellClassName: TD_PADDING,
                headerClassName: TH_PADDING
            },
            (team) => (
                <div className="flex items-center gap-3 min-w-0">
                    <TeamColorTile name={team.name} />
                    <span className="text-sm font-semibold text-gray-800 truncate">
                        {team.name}
                    </span>
                </div>
            ),
            { getTitle: (team) => team.name }
        ),
        statusColumn<Role, TeamsSortBy>({
            key: 'scope',
            label: 'Scope',
            width: '25%',
            cellClassName: TD_PADDING,
            headerClassName: TH_PADDING,
            value: (team) => team.scope,
            getLabel: (team) => TEAM_SCOPE_LABEL[team.scope],
            tone: TEAM_SCOPE_TONE
        }),
        buildColumn<Role, TeamsSortBy>(
            {
                key: 'created',
                label: 'Created',
                width: '30%',
                sortable: true,
                sortKey: 'created',
                cellClassName: TD_PADDING,
                headerClassName: TH_PADDING
            },
            (team) => formatRelativeActivity(team.createdAt) ?? EMPTY_VALUE,
            { getTitle: (team) => formatCreatedDate(team.createdAt) || undefined }
        )
    ];
}
